import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { clockOf, minutesOf, type FlowAssessment, type FlowCoral } from '../../services/flowSurveyService';
import { flowCoralName } from './FlowCoralList';
import '../lighting/Lighting.css';

export interface FlowDayViewProps {
  assessment: FlowAssessment;
  corals: readonly FlowCoral[];
  measuredClock: string | null;
  /** Show the map at this time of day. */
  onShow: (clock: string) => void;
  /** Start a linked re-measurement, prefilled with this time. */
  onMeasureAgain: (clock: string | null) => void;
  /** No pump has a day schedule yet: where to enter them. */
  onOpenSchedules: () => void;
}

/** The middle of a slot as "HH:MM" (a slot may run past midnight). */
function middleOf(start: string, end: string): string {
  const s = minutesOf(start);
  let e = minutesOf(end);
  if (e <= s) e += 1440;
  return clockOf(Math.floor((s + e) / 2 / 30) * 30);
}

/**
 * The survey over the day: one row per stretch in which every pump runs one
 * programme — measured or estimated, dead spots, and corals that get too little
 * or too much flow then. Corals that are fine as measured but drop later (night
 * mode) are named with the offer to measure again at that time.
 */
export function FlowDayView({ assessment, corals, measuredClock, onShow, onMeasureAgain, onOpenSchedules }: FlowDayViewProps) {
  const { t } = useTranslation();
  const nameOf = new Map(corals.map((c, i) => [c.id, `${i + 1}. ${flowCoralName(c)}`]));

  if (assessment.scheduledPumpIds.length === 0) {
    return (
      <Card>
        <Stack gap="3">
          <Text>{t('flow.day.noSchedule')}</Text>
          <Inline gap="2">
            <Button type="button" variant="secondary" onClick={onOpenSchedules}>{t('flow.day.openSchedules')}</Button>
            <Button type="button" variant="ghost" onClick={() => onMeasureAgain(null)}>{t('flow.copy.open')}</Button>
          </Inline>
        </Stack>
      </Card>
    );
  }

  const firstDrop = assessment.timeDrops[0]?.slots[0] ?? null;
  return (
    <Stack gap="4">
      {assessment.timeDrops.length > 0 && (
        <Alert
          tone="warning"
          live="off"
          title={t('flow.day.dropsTitle')}
          titleLevel={3}
          action={<Button type="button" size="sm" variant="secondary" onClick={() => onMeasureAgain(firstDrop)}>{t('flow.day.measureAgain', { time: firstDrop })}</Button>}
        >
          {t('flow.day.drops', {
            corals: assessment.timeDrops.map((d) => `${nameOf.get(d.coralId) ?? ''} (${d.slots.join(', ')})`).join('; '),
          })}
        </Alert>
      )}
      <Card>
        <Stack gap="3">
          {!measuredClock && <Text size="sm" color="secondary">{t('flow.day.noClock')}</Text>}
          <ol className="lighting__list" data-testid="flow-day">
            {assessment.timeline.map((slot) => {
              const weak = slot.corals.filter((c) => c.status === 'too_weak').map((c) => nameOf.get(c.coralId));
              const strong = slot.corals.filter((c) => c.status === 'too_strong').map((c) => nameOf.get(c.coralId));
              const range = `${slot.start}–${slot.end}`;
              return (
                <li key={slot.start} className="lighting__row">
                  <Stack gap="1" className="lighting__row-text">
                    <Inline gap="2" align="center">
                      <span className="flow-map__stat-value flow-map__stat-value--small">{range}</span>
                      <Badge tone={slot.measured ? 'success' : 'neutral'}>{t(slot.measured ? 'flow.day.measured' : 'flow.day.estimated')}</Badge>
                    </Inline>
                    <Text size="sm" color="secondary">{t('flow.day.counts', { dead: slot.deadSpotCount, blasting: slot.blastingCount })}</Text>
                    {weak.length > 0 && <Text size="sm">{t('flow.day.weak', { corals: weak.join(', ') })}</Text>}
                    {strong.length > 0 && <Text size="sm">{t('flow.day.strong', { corals: strong.join(', ') })}</Text>}
                  </Stack>
                  <Button type="button" size="sm" variant="ghost" onClick={() => onShow(middleOf(slot.start, slot.end))} aria-label={t('flow.day.showLabel', { range })}>
                    {t('flow.day.show')}
                  </Button>
                </li>
              );
            })}
          </ol>
          <Text color="muted" variant="caption">{t('flow.day.note')}</Text>
          <Inline gap="2">
            <Button type="button" variant="ghost" onClick={() => onMeasureAgain(null)}>{t('flow.copy.open')}</Button>
          </Inline>
        </Stack>
      </Card>
    </Stack>
  );
}

export default FlowDayView;
